import { Button } from '@progress/kendo-react-buttons';
import { Checkbox, TextBox } from '@progress/kendo-react-inputs';
import { SaveOutline } from '../../../../../icons/icons';
import { UdViewModel, UDViewType } from '../../../../../udView/udview';
import { LoadingIndicatorCircular } from '../../../../components/indicators/LoadingIndicatorCircular';
import { DropdownList, DropdownListWithValueField } from '../../../../components/inputs/DropdownListWithValueField';
import { useWorkspaceItemContext } from '../../../../contexts/workspace/workspace-item-context';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store-hooks';
import { createView, updateView } from '../../../../state/slices/views-slice';
import { store } from '../../../../state/store';
import { useFormCheckboxValue, useFormDropdownValue, useFormStringValue } from '../../hooks/forms';

export function UdViewEdit(props: { onClose?: () => void; edit: UdViewModel | null }) {
    const tutorial = useAppSelector((x) => x.global.tutorial);
    const companyFeaturesEnabled = useAppSelector((x) => x.user.rights.companyFeaturesEnabled);
    const canEditCompanyViews = useAppSelector((x) => x.user.rights.canEditCompanyViews);
    const workspaceId = useWorkspaceItemContext().id;
    const name = useFormStringValue(props.edit?.name ?? (tutorial ? 'My View' : ''));
    const type = useFormDropdownValue(ViewTypeOptions, props.edit?.type ?? UDViewType.user);
    const includeInSlideShow = useFormCheckboxValue(props.edit?.includeInSlideshow ?? false);
    const busy = useAppSelector((x) => x.views.busy);
    const dispatch = useAppDispatch();

    const handleSave = async () => {
        try {
            const editItem = props.edit;
            const workspace = store.getState().workspace;
            const workspaceSelectedView = workspace.items[workspace.selectedId].view;

            if (editItem) {
                // If we're trying to change currently selected view, use the modified copy to save instead of unmodified from ud view list
                const editedView = editItem.id === workspaceSelectedView.id ? workspaceSelectedView : editItem;
                await dispatch(
                    updateView({
                        workspaceId: workspaceId,
                        view: {
                            ...editedView,
                            name: name.value,
                            type: type.value,
                            includeInSlideshow: includeInSlideShow.value
                        }
                    })
                ).unwrap();
            } else {
                const { id } = await dispatch(
                    createView({
                        workspaceId: workspaceId,
                        view: {
                            ...workspaceSelectedView,
                            name: name.value,
                            type: type.value,
                            includeInSlideshow: includeInSlideShow.value
                        }
                    })
                ).unwrap();

                if (tutorial) {
                    window.tutorialViewId = id;
                }
            }
            props.onClose?.();
        } catch {
            // Handled already
        }
    };

    return (
        <div className="position-relative">
            <div className="p-2 d-flex flex-column gap-2" style={{ minWidth: 300 }}>
                <div id="udViewName">
                    <div>Name:</div>
                    <TextBox {...name} placeholder="Name" autoFocus />
                </div>

                {companyFeaturesEnabled && (
                    <div id="viewType">
                        <div>Type:</div>
                        <DropdownListWithValueField disabled={!canEditCompanyViews} {...type} data={ViewTypeOptions} />
                    </div>
                )}

                <Checkbox id="includeInSlideshow" {...includeInSlideShow} label="Include in slideshow" />
            </div>

            <div className="d-flex gap-2 bg-color p-2 border-top">
                <Button className="flex-grow-1" onClick={props.onClose}>
                    cancel
                </Button>
                <Button
                    id="viewSave"
                    className="flex-grow-1"
                    themeColor="primary"
                    svgIcon={SaveOutline}
                    onClick={handleSave}
                    disabled={!name.value}>
                    save
                </Button>
            </div>
            {busy && (
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ background: 'rgba(0,0,0,0.33)', position: 'absolute', inset: 0 }}>
                    <LoadingIndicatorCircular className="m-auto" />
                </div>
            )}
        </div>
    );
}

const ViewTypeOptions: DropdownList<UDViewType> = [
    { value: UDViewType.user, text: 'User' },
    { value: UDViewType.company, text: 'Company' }
];
